import React from "react";

/**
 * Elegant circular sustainability score with a gradient arc, animated draw
 * and (optional) letter-grade centrepiece. Colour palette shifts smoothly
 * from amber → emerald so a high score physically feels greener.
 */

const gradientFor = (score) => {
  if (score >= 80) return { a: "#10B981", b: "#059669", text: "#047857", grade: "A" };
  if (score >= 65) return { a: "#22D3A5", b: "#10B981", text: "#065F46", grade: "B" };
  if (score >= 50) return { a: "#F59E0B", b: "#D97706", text: "#92400E", grade: "C" };
  if (score >= 35) return { a: "#F97316", b: "#EA580C", text: "#9A3412", grade: "D" };
  return { a: "#EF4444", b: "#DC2626", text: "#991B1B", grade: "E" };
};

const SustainabilityScore = ({
  score = 80,
  size = 76,
  stroke = 7,
  showLabel = true,
  showGrade = false,
  variant = "arc", // "arc" | "chip"
}) => {
  const s = Math.max(0, Math.min(100, Math.round(score || 0)));
  const g = gradientFor(s);

  if (variant === "chip") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 text-xs font-bold"
        style={{
          background: `linear-gradient(135deg,${g.a}22 0%,${g.b}44 100%)`,
          color: g.text,
          boxShadow: `inset 0 0 0 1px ${g.b}55`,
        }}
        data-testid="sustainability-score-chip"
      >
        <span
          className="grid place-items-center h-6 w-6 rounded-full text-white mono text-[11px] font-bold"
          style={{ background: `linear-gradient(135deg,${g.a} 0%,${g.b} 100%)` }}
        >
          {s}
        </span>
        <span className="uppercase tracking-widest text-[10px] opacity-80">Eco score</span>
      </span>
    );
  }

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (s / 100) * circumference;
  const gradId = `ews-grad-${g.grade}`;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      data-testid="sustainability-score-widget"
    >
      <svg width={size} height={size} className="-rotate-90 drop-shadow-[0_2px_8px_rgba(16,185,129,0.15)]">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={g.a} />
            <stop offset="100%" stopColor={g.b} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-muted opacity-30"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.16, 1, 0.3, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        {showGrade ? (
          <>
            <span className="mono font-black" style={{ fontSize: size * 0.36, color: g.text, letterSpacing: "-0.02em" }}>
              {g.grade}
            </span>
            <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
              {s}/100
            </span>
          </>
        ) : (
          <>
            <span
              className="mono font-bold"
              style={{ fontSize: size * 0.3, color: g.text, letterSpacing: "-0.02em" }}
            >
              {s}
            </span>
            {showLabel && (
              <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
                Eco · /100
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SustainabilityScore;
